/**
 * The Customers list — one row per paying tenant, identity and health only.
 *
 * ── What changed, and why the row got SMALLER ───────────────────────────
 *
 * This replaces a table that was simultaneously a list and an editor: five
 * columns, two editors hidden in a per-row expander, and source comments
 * explaining that a column had to be REMOVED and 20px shaved off the tenant
 * name to stop it scrolling horizontally. It was at its width budget, and the
 * redesign needs to show plan, subscription health and headcount as well.
 *
 * So the row stops being an editor. Everything editable moved to the customer
 * DETAIL page, which is also the thing an operator actually wants: one screen
 * per customer instead of three screens and two tenant pickers. What is left
 * here is the question a list should answer — "who needs my attention" — and
 * the answer is legible at a glance rather than assembled from five columns.
 *
 * Pagination stays server-side and page-scoped: the endpoint's spend aggregate
 * is a LATERAL join over the page's rows, so "just fetch them all" would scan
 * the whole period across every tenant.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useRefreshWhenShown, useBillingCurrency } from '@papan/core-ui/ui'
import { formatMoney, PLATFORM_DATE_LOCALE, deploymentDefaultAllowsPersonalDrives } from '@papan/types'
import { budgetApi, type AdminTenantsResponse, type AdminTenantRow } from '../../client/budget'
import { formatBytes, Section } from './bits'
import { HealthBadge, UploadsPausedChip, IdentityOnlyChip, capState } from './customerBits'
import { NewCustomerDialog } from './NewCustomerDialog'

const PAGE_SIZE = 25

type TenantFilter = 'organization' | 'personal'

export function CustomersList({
  isPlatformAdmin,
  activeTenantId,
  canCreate,
  onOpen,
}: {
  isPlatformAdmin: boolean
  /** The workspace the admin is currently in — flagged so an edit on that
   *  customer is not mistaken for a setting of the org they are viewing. */
  activeTenantId?: string
  /** `customers:write` — creating a customer is the same grant as changing
   *  one, gated on what the action DOES rather than on the screen it is on. */
  canCreate?: boolean
  onOpen: (tenantId: string) => void
}) {
  const { t } = useTranslation('core-ui')
  const [page, setPage] = useState<AdminTenantsResponse | null>(null)
  const [offset, setOffset] = useState(0)
  // Organizations by default: every user has a personal tenant, so the
  // unfiltered list is mostly personal noise a platform admin rarely needs.
  const [filter, setFilter] = useState<TenantFilter>('organization')
  const [planFilter, setPlanFilter] = useState<string>('')
  const [search, setSearch] = useState('')
  // With a search: how many tenants of the OTHER tab also match. The list opens
  // on Organizations, so typing a person's email there would otherwise answer
  // "No matches" with nothing saying to look under Personal.
  // Keyed by the tab + search + plan it was counted for, and shown only while
  // they still match — never a previous search's count, or the other tab's.
  const [otherTab, setOtherTab] = useState<{ key: string; total: number } | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  // `null` = still loading; `false` = personal drives are off on this deployment
  // (default_personal_tenant_mode === 'identity-only'), so the Personal/Org
  // segmentation is hidden and the list is flat (no type filter).
  const [personalAllowed, setPersonalAllowed] = useState<boolean | null>(null)
  const [plans, setPlans] = useState<Array<{ id: string; name: string }>>([])

  useEffect(() => {
    const id = setTimeout(() => { setSearch(searchInput.trim()); setOffset(0) }, 300)
    return () => clearTimeout(id)
  }, [searchInput])

  // Deployment shape: whether personal drives exist (drives the filter chrome)
  // and the plan list (populates the plan filter). Both advisory — a failure
  // leaves the list fully functional, just without those affordances.
  useEffect(() => {
    if (!isPlatformAdmin) return
    void (async () => {
      try {
        const s = await budgetApi.getPlatformSettings()
        // Sanctioned reader — never compare the raw mode column (guarded by
        // no-raw-tenant-mode-reads.test.ts).
        setPersonalAllowed(deploymentDefaultAllowsPersonalDrives(s.resolved?.defaultPersonalTenantMode))
      } catch { setPersonalAllowed(true) }
      try {
        const { catalogAdminApi } = await import('@papan/billing/client')
        const res = await catalogAdminApi.listPlans()
        setPlans(res.plans.map((p) => ({ id: p.id, name: p.name })))
      } catch { /* leave the plan filter empty */ }
    })()
  }, [isPlatformAdmin])

  // Monotonic request id: `load` fires from several effects (including once
  // before `personalAllowed` resolves and once after), so a slower earlier
  // request must not overwrite a newer one's result.
  const loadSeq = useRef(0)
  const load = useCallback(async (nextOffset: number) => {
    const seq = ++loadSeq.current
    setLoading(true)
    const key = `${filter}|${search}|${planFilter}`
    // The other tab's count runs ALONGSIDE the page, not after it, so a search
    // keystroke doesn't keep the list dimmed for a second round trip.
    const otherReq = search && personalAllowed !== false
      ? budgetApi.listTenants({
          limit: 1,
          type: filter === 'organization' ? 'personal' : 'organization',
          planId: planFilter || undefined,
          search,
        }).catch(() => null)
      : null
    try {
      const res = await budgetApi.listTenants({
        limit: PAGE_SIZE,
        offset: nextOffset,
        // When personal drives are off the deployment has no meaningful personal
        // tenants to browse, so the list shows ONLY organizations (not a flat
        // all-tenants list).
        type: personalAllowed === false ? 'organization' : filter,
        planId: planFilter || undefined,
        search: search || undefined,
      })
      if (loadSeq.current !== seq) return // a newer load superseded this one
      setPage(res)
      setError(null)
      if (otherReq) {
        void otherReq.then((other) => {
          if (loadSeq.current === seq) setOtherTab({ key, total: other?.total ?? 0 })
        })
      } else {
        setOtherTab(null)
      }
    } catch (e: any) {
      if (loadSeq.current !== seq) return
      setError(e?.message || 'Failed to load tenants')
    } finally {
      if (loadSeq.current === seq) setLoading(false)
    }
  }, [filter, planFilter, personalAllowed, search])

  useEffect(() => { if (isPlatformAdmin) load(offset); else setLoading(false) }, [isPlatformAdmin, offset, load])

  // The deck stays MOUNTED behind `display: none` when you switch away, so
  // without this the page serves whatever was true the first time it was
  // opened — no error, no staleness marker, and only a full reload fixes it.
  // See `DeckVisibility`.
  useRefreshWhenShown(() => { if (isPlatformAdmin) void load(offset) })

  if (!isPlatformAdmin) {
    return <div className="p-4 themed-text-muted text-sm">{t('settings.budget.noPlatformAdmin')}</div>
  }
  if (!page) {
    if (loading) return <div className="p-4 themed-text-muted text-sm">{t('common:loading')}</div>
    return <div className="p-4 text-sm text-red-400">{error}</div>
  }

  const from = page.total === 0 ? 0 : page.offset + 1
  const to = Math.min(page.offset + page.limit, page.total)

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="themed-text text-lg font-medium mb-1">
            {t('settings.platform.customers.title', { defaultValue: 'Tenants' })}
          </h2>
          <p className="themed-text-muted text-sm">
            {t('settings.platform.customers.desc', {
              defaultValue: 'Every tenant on this deployment. Open one to see and change everything about it.',
            })}{' '}
            <span className="themed-text-muted">({page.period})</span>
          </p>
        </div>
        {canCreate && (
          <button
            onClick={() => setCreating(true)}
            className="shrink-0 px-3 py-1.5 text-xs rounded themed-btn-primary"
          >
            {t('settings.platform.newOrg.open', { defaultValue: '+ New organization' })}
          </button>
        )}
      </div>

      {creating && (
        <NewCustomerDialog
          onClose={() => setCreating(false)}
          // Straight to the customer it just made. Provisioning and then having
          // to hunt for the tenant in a paginated list would undo the point of
          // the customer being the object.
          onCreated={(tenantId) => { setCreating(false); onOpen(tenantId) }}
        />
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {/* Personal/Organizations segmentation — hidden entirely when personal
            drives are off on this deployment (the distinction is then moot).
            'All' was removed: the two meaningful buckets are enough. */}
        {personalAllowed !== false && (
          <div className="flex items-center gap-1">
            {(['organization', 'personal'] as const).map((f) => (
              <button
                key={f}
                onClick={() => { setFilter(f); setOffset(0) }}
                className={`px-2.5 py-1 text-xs rounded transition-colors ${
                  filter === f ? 'themed-btn-primary' : 'themed-btn-secondary'
                }`}
              >
                {f === 'organization'
                  ? t('settings.budget.platform.filterOrgs')
                  : t('settings.budget.platform.filterPersonal')}
              </button>
            ))}
          </div>
        )}
        {/* Filter by plan (redesign). Empty = every plan. */}
        {plans.length > 0 && (
          <select
            value={planFilter}
            onChange={(e) => { setPlanFilter(e.target.value); setOffset(0) }}
            aria-label={t('settings.budget.platform.filterPlan', { defaultValue: 'Filter by plan' })}
            className="themed-input border rounded px-1.5 py-1 text-xs outline-none focus:border-blue-500"
          >
            <option value="">{t('settings.budget.platform.filterPlanAll', { defaultValue: 'All plans' })}</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        )}
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t('settings.budget.platform.searchPh')}
          aria-label={t('settings.budget.platform.searchPh')}
          className="themed-input border rounded px-2 py-1 text-xs w-56 outline-none focus:border-blue-500"
        />
      </div>

      <Section
        title={t('settings.platform.customers.listTitle', { defaultValue: 'All tenants' })}
        desc={t('settings.platform.customers.listDesc', {
          defaultValue: 'Spend is the total metered cost this period, against the ceiling set for that tenant.',
        })}
      >
        {/* Column headers (F-27).
            ────────────────────────────────────────────────────────────────
            The rows carry two unlabelled progress bars — spend against the
            ceiling, and storage against the quota — and the storage one was
            never named anywhere on the screen. A reader had to infer both from
            the numbers above them.

            Not a `<table>`: the rows are BUTTONS (the whole row opens the
            tenant), and a button is not valid inside a `<td>` without losing the
            row-level click target. So this is a header strip aligned to the same
            widths, hidden from assistive tech — each row already names its own
            figures through its labels, and a floating header row would be read
            as content. */}
        {page.tenants.length > 0 && (
          // Hidden below `sm`, where the row stacks (F-25) and a three-column
          // header would label columns that are no longer side by side.
          <div aria-hidden className="hidden sm:flex items-center gap-3 px-3 pb-1 -mt-1">
            <div className="flex-1 min-w-0 text-[10px] uppercase tracking-wide themed-text-muted">
              {t('settings.platform.customers.colTenant', { defaultValue: 'Tenant' })}
            </div>
            <div className="w-[170px] shrink-0 text-right text-[10px] uppercase tracking-wide themed-text-muted">
              {t('settings.platform.customers.colSpend', { defaultValue: 'Spend / ceiling' })}
            </div>
            <div className="w-[110px] shrink-0 text-right text-[10px] uppercase tracking-wide themed-text-muted">
              {t('settings.platform.customers.colStorage', { defaultValue: 'Storage' })}
            </div>
            {/* Matches the chevron each row ends with, so the columns line up. */}
            <span className="text-lg leading-none opacity-0">›</span>
          </div>
        )}

        {search && otherTab && otherTab.key === `${filter}|${search}|${planFilter}` && otherTab.total > 0 && (
          <div className="mb-2 text-[12px] themed-text-secondary flex items-center gap-2 flex-wrap" data-testid="tenant-other-tab-matches">
            <span id="tenant-other-tab-matches-text">
              {filter === 'organization'
                ? t('settings.platform.customers.alsoMatchPersonal', { defaultValue: 'Also matching in Personal: {{count}}', count: otherTab.total })
                : t('settings.platform.customers.alsoMatchOrgs', { defaultValue: 'Also matching in Organizations: {{count}}', count: otherTab.total })}
            </span>
            <button
              type="button"
              onClick={() => { setFilter(filter === 'organization' ? 'personal' : 'organization'); setOffset(0) }}
              className="text-blue-400 hover:underline min-h-[28px]"
              // The name stays the visible "Show" (voice control: "click Show");
              // the count sentence is its description.
              aria-describedby="tenant-other-tab-matches-text"
            >
              {t('settings.platform.customers.showMatches', { defaultValue: 'Show' })}
            </button>
          </div>
        )}
        <div
          data-testid="customers-list"
          className={`themed-bg-secondary rounded-lg overflow-hidden transition-opacity ${loading ? 'opacity-60' : ''}`}
          aria-busy={loading}
        >
          {page.tenants.length === 0 && (
            <div className="px-3 py-4 themed-text-muted text-sm">
              {/* "No matches" when a search, plan filter, or the Personal segment
                  narrowed the set; "no data" for the baseline organizations view
                  (which is also what identity-only mode forces). */}
              {search || planFilter || filter === 'personal'
                ? t('settings.budget.platform.noMatches')
                : t('settings.budget.noData')}
            </div>
          )}
          {page.tenants.map((row) => (
            <CustomerRow
              key={row.id}
              row={row}
              isCurrent={row.id === activeTenantId}
              onOpen={() => onOpen(row.id)}
            />
          ))}
        </div>

        <div className="flex items-center gap-3 mt-3">
          <button
            disabled={page.offset === 0}
            onClick={() => setOffset(Math.max(0, page.offset - PAGE_SIZE))}
            className="px-3 py-1.5 text-xs rounded themed-btn-secondary disabled:opacity-40"
          >
            {t('settings.budget.prev')}
          </button>
          <span className="text-xs themed-text-muted">
            {t('settings.budget.pageRange', { from, to, total: page.total })}
          </span>
          <button
            disabled={to >= page.total}
            onClick={() => setOffset(page.offset + PAGE_SIZE)}
            className="px-3 py-1.5 text-xs rounded themed-btn-secondary disabled:opacity-40"
          >
            {t('settings.budget.next')}
          </button>
        </div>
      </Section>

    </div>
  )
}

/**
 * One customer. A button, not a table row: the whole thing is the click target
 * for the detail page, and there is nothing else interactive inside it — which
 * is exactly what removing the inline editors bought.
 */
/**
 * "Created 3d ago" — a relative age, because the absolute date answers a question
 * nobody is asking on a list sorted by recency. The exact timestamp is the title
 * attribute for when someone does want it.
 *
 * Whole days only: an operator scanning for "who is new this week" does not need
 * hours, and a ticking relative time on a paged list is churn.
 */
function relativeAge(iso: string, t: (k: string, o?: Record<string, unknown>) => string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  if (!Number.isFinite(days) || days < 0) return t('settings.platform.customers.createdToday', { defaultValue: 'new' })
  if (days === 0) return t('settings.platform.customers.createdToday', { defaultValue: 'new' })
  return t('settings.platform.customers.createdDaysAgo', { defaultValue: '{{n}}d old', n: days })
}

function CustomerRow({
  row, isCurrent, onOpen,
}: {
  row: AdminTenantRow
  isCurrent: boolean
  onOpen: () => void
}) {
  const { t } = useTranslation('core-ui')
  // ── The display-currency second line is GONE ─────────────────────────
  //
  // It rendered "~RM55.89" beneath the primary figure, converting a canonical
  // USD amount at the tenant's display rate. Both halves of that stopped being
  // true: the primary figure is now the BILLING-native charge, so converting
  // it again by a display rate is a second conversion of an amount that is
  // already in the customer's own currency — a plausible number nobody is
  // charged. The display layer is parked (plan section 7), not deleted, so the
  // fields stay on the wire; nothing renders them here.
  const moneyOpts = { currency: useBillingCurrency() }

  const cap = capState(row)
  const storagePct = row.storageUsedBytes != null && row.storageQuotaBytes > 0
    ? Math.min(100, (row.storageUsedBytes / row.storageQuotaBytes) * 100)
    : null

  return (
    /*
     * F-25 — the row STACKS below `sm`.
     *
     * The two figure columns are fixed at 170px and 110px, which with the gaps,
     * the padding and the chevron leaves ~50px for the identity at a 390px
     * viewport: the name truncated to "Northwind Le…", the metadata line wrapped
     * to four rows, and the row itself measured 468px wide — i.e. the page
     * scrolled sideways, which is the very defect Phase 0 set out to remove.
     *
     * `sm:contents` is what keeps ONE markup for both: below the breakpoint the
     * wrapper is a real flex row holding the two figure columns beneath the
     * identity; at `sm` and up it collapses to `display: contents`, so those two
     * columns become direct children of the row again and line up under the
     * header strip exactly as before. No duplicated subtree to drift.
     */
    <div className="border-b themed-border last:border-b-0">
    <button
      onClick={onOpen}
      className={`w-full text-left px-3 py-3 transition-colors flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 ${
        isCurrent ? 'bg-blue-500/[0.06]' : 'hover:themed-bg-tertiary'
      }`}
    >
      {/* Identity */}
      <div className="min-w-0 flex-1 w-full">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="themed-text font-medium truncate max-w-full sm:max-w-[240px]" title={row.name}>{row.name}</span>
          {isCurrent && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 whitespace-nowrap"
              title={t('settings.budget.platform.currentHint')}
            >
              {t('settings.budget.platform.current')}
            </span>
          )}
          <HealthBadge row={row} />
          <UploadsPausedChip row={row} />
          <IdentityOnlyChip row={row} />
        </div>
        <TenantEmailLine row={row} />
        <div className="text-[11px] themed-text-muted font-mono mt-0.5 flex items-center gap-2 flex-wrap">
          <span>{row.type}</span>
          <span>·</span>
          <span>
            {/* `planLabel` is what to SHOW — the preset's name, "Custom", or the
                operator's own label for a bespoke arrangement (0164). `planName`
                stays the PRESET's name and is deliberately not rendered here:
                showing both would be the two-meanings conflation that
                plan-preset.ts exists to prevent. Falls back for older responses. */}
            {row.planLabel ?? row.planName ?? '—'}
            {/* A plan the payment provider has never heard of must not look
                identical to one it bills for — see `planSource` (0157). */}
            {row.planSource === 'override' && (
              <span className="ml-1 themed-text-muted">
                ({t('settings.platform.customer.planSetHere', { defaultValue: 'set here' })})
              </span>
            )}
          </span>
          {/* Only when the operator NAMED the arrangement. An unnamed Custom
              tenant already renders its plan as "Custom", so the chip beside it
              was the same fact twice; a named one reads "Demo Co. Enterprise ·
              customised", where the chip is what explains why that name is there.
              Deliberately NOT a claim that they differ from the preset — the flag
              means hand-edited — so the tooltip says exactly that. */}
          {row.planLabelSource === 'custom-named' && (
            <>
              <span>·</span>
              <span
                className="themed-text-muted"
                title={t('settings.platform.customers.customHint', {
                  defaultValue: 'Hand-edited since it was created. Preset updates skip it by default.',
                })}
              >
                {t('settings.platform.customers.customised', { defaultValue: 'customised' })}
              </span>
            </>
          )}
          {/* The query has always been ORDER BY created_at DESC, so new customers
              were top-of-list with nothing on screen saying so — and "0 members"
              reads the same for a brand-new org and an abandoned one, because
              provisioning invites rather than seats. */}
          {row.createdAt && (
            <>
              <span>·</span>
              <span title={new Date(row.createdAt).toLocaleString(PLATFORM_DATE_LOCALE)}>
                {relativeAge(row.createdAt, t)}
              </span>
            </>
          )}
          <span>·</span>
          <span title={t('settings.platform.customer.members', { defaultValue: 'Members' })}>
            {t('settings.platform.customers.memberCount', {
              defaultValue: '{{count}} member',
              defaultValue_other: '{{count}} members',
              count: row.memberCount,
            })}
          </span>
        </div>
      </div>

      {/* Stacked below `sm`, transparent at `sm` and up — see the note on the
          row above. The figures stay right-aligned inside their own widths in
          both layouts, so the numbers still form columns when stacked. */}
      <div className="flex items-start gap-3 sm:contents">

      {/* Spend against the ceiling. The bar is only drawn when a ceiling
          EXISTS — an unlimited tenant has no denominator, and a full-looking
          bar against no cap would be a lie in the alarming direction. */}
      <div className="w-[170px] shrink-0 text-left sm:text-right">
        {/* The header strip is hidden when stacked, so each column names itself
            there instead — otherwise the storage bar goes back to being the
            unlabelled bar F-27 was about, just at a narrower width. */}
        <div className="sm:hidden text-[10px] uppercase tracking-wide themed-text-muted">
          {t('settings.platform.customers.colSpend', { defaultValue: 'Spend / ceiling' })}
        </div>
        <div className="themed-text text-sm font-medium tabular-nums">{formatMoney(row.periodCostBillingMicro, moneyOpts)}</div>
        {cap.total != null ? (
          <>
            <div className="mt-1 h-1 w-full rounded-full themed-bg-tertiary overflow-hidden">
              <div
                className={`h-full rounded-full ${
                  cap.pct >= 100 ? 'bg-red-500' : cap.pct >= 80 ? 'bg-amber-500' : 'bg-blue-500'
                }`}
                style={{ width: `${Math.min(100, cap.pct)}%` }}
              />
            </div>
            <div className="text-[10px] themed-text-muted mt-0.5 tabular-nums">
              {t('settings.platform.customers.ofCap', {
                defaultValue: 'of {{cap}}',
                cap: formatMoney(cap.total, moneyOpts),
              })}
            </div>
          </>
        ) : (
          <div className="text-[10px] themed-text-muted mt-1">
            {cap.blocked
              ? t('settings.platform.customers.blocked', { defaultValue: 'AI blocked' })
              : t('settings.budget.unlimited', { defaultValue: 'No ceiling' })}
          </div>
        )}
      </div>

      {/* Storage. used/quota is exact, so a fill bar is honest here. */}
      <div className="w-[110px] shrink-0 text-left sm:text-right">
        <div className="sm:hidden text-[10px] uppercase tracking-wide themed-text-muted">
          {t('settings.platform.customers.colStorage', { defaultValue: 'Storage' })}
        </div>
        <div className="text-[11px] themed-text-muted tabular-nums">
          {row.storageUsedBytes == null
            ? t('settings.budget.notMeasured')
            : `${formatBytes(row.storageUsedBytes)} / ${formatBytes(row.storageQuotaBytes)}`}
        </div>
        {storagePct != null && (
          <div className="mt-1 h-1 w-full rounded-full themed-bg-tertiary overflow-hidden">
            <div
              className={`h-full rounded-full ${
                storagePct >= 100 ? 'bg-red-500' : storagePct >= 80 ? 'bg-amber-500' : 'bg-blue-500'
              }`}
              style={{ width: `${storagePct}%` }}
            />
          </div>
        )}
      </div>

      </div>

      {/* Hidden when stacked: the whole row is the click target, so a chevron
          floating below the figures would point at nothing in particular. */}
      <span aria-hidden className="hidden sm:block themed-text-muted shrink-0 text-lg leading-none">›</span>
    </button>
    {/* `otherEmailCount` absent on an older response: offer it anyway. */}
    {row.type === 'personal' && row.primaryEmail && row.otherEmailCount !== 0 && <OtherEmails row={row} />}
    </div>
  )
}

/**
 * Who this tenant is, under its name: a personal tenant's MAIN email, or an
 * organization's owner (oldest first, "+N" for more). Two tenants can share a
 * name — "Dev User" twice — and an email search must show why a row matched.
 */
function TenantEmailLine({ row }: { row: AdminTenantRow }) {
  const { t } = useTranslation('core-ui')
  if (row.type === 'personal') {
    if (!row.primaryEmail) return null
    return (
      <div id={`tenant-email-${row.id}`} className="text-[12px] themed-text-secondary mt-0.5 flex items-center min-w-0" data-testid="tenant-primary-email">
        {/* The chip sits OUTSIDE the truncating span: a long address must not
            clip the warning away. */}
        <span className="truncate min-w-0" title={row.primaryEmail}>{row.primaryEmail}</span>
        {row.primaryEmailVerified === false && <UnverifiedChip />}
      </div>
    )
  }
  // An owner who has joined; otherwise one still invited (a console-provisioned
  // org has no member until its owner signs up).
  // Joined owners first, then owners still invited — both, so an email search
  // that matched an invited co-owner shows it.
  // An email search that matched a later owner puts that owner first.
  const m = row.matchedEmail?.toLowerCase()
  const matchFirst = (list: string[]) => {
    const hit = m ? list.find((e) => e.toLowerCase() === m) : undefined
    return hit ? [hit, ...list.filter((e) => e !== hit)] : list
  }
  const joined = matchFirst(row.ownerEmails ?? [])
  const invited = matchFirst(row.invitedOwnerEmails ?? [])
  if (joined.length === 0 && invited.length === 0) return null
  const ownerLabel = t('settings.platform.customers.ownerLabel', { defaultValue: 'Owner' })
  const invitedLabel = t('settings.platform.customers.invitedOwnerLabel', { defaultValue: 'Invited owner' })
  const matchedInvited = !!m && invited.some((e) => e.toLowerCase() === m)
  const joinedSeg = { label: ownerLabel, list: joined }
  const invitedSeg = { label: invitedLabel, list: invited }
  const segments = (matchedInvited ? [invitedSeg, joinedSeg] : [joinedSeg, invitedSeg])
    .filter((seg) => seg.list.length > 0)
  const title = [
    ...joined.map((e) => `${ownerLabel}: ${e}`),
    ...invited.map((e) => `${invitedLabel}: ${e}`),
  ].join('\n')
  return (
    <div className="text-[12px] themed-text-secondary truncate mt-0.5" title={title} data-testid="tenant-owner-email">
      {/* The group holding the address the search matched goes FIRST, so a
          narrow row never truncates the reason it matched away. */}
      {segments.map((seg, i) => (
        <span key={seg.label}>
          {i > 0 && <span className="themed-text-muted"> · </span>}
          <span className="themed-text-muted">{seg.label}: </span>
          {seg.list[0]}
          {seg.list.length > 1 && <span className="themed-text-muted"> +{seg.list.length - 1}</span>}
        </span>
      ))}
    </div>
  )
}

/** The account never proved this address — it may not be the person it names (#595). */
function UnverifiedChip() {
  const { t } = useTranslation('core-ui')
  return (
    <span className="ml-1.5 shrink-0 whitespace-nowrap text-[10px] px-1.5 py-0.5 rounded themed-bg-tertiary themed-chip-warn" data-testid="tenant-email-unverified">
      {t('settings.platform.customers.unverifiedEmail', { defaultValue: 'Unverified' })}
    </span>
  )
}

/**
 * "Other emails (N)" for a personal tenant: the account's VERIFIED emails other
 * than the main one (which the row already shows), fetched when opened — the
 * list carries only the count. Hidden when there are none. When an email
 * search matched one of them, it opens by itself with that address
 * highlighted, so the row shows why it matched. A sibling of the row's button,
 * not inside it — a button inside a button is invalid and would open the tenant.
 */
function OtherEmails({ row }: { row: AdminTenantRow }) {
  const { t } = useTranslation('core-ui')
  const main = row.primaryEmail?.toLowerCase() ?? ''
  const matched = row.matchedEmail && row.matchedEmail.toLowerCase() !== main
    ? row.matchedEmail.toLowerCase()
    : null
  const [open, setOpen] = useState(false)
  const [emails, setEmails] = useState<Array<{ email: string; verified: boolean }> | null>(null)
  const [failed, setFailed] = useState(false)
  // Only the LATEST request may set state: open → close → open fires two, and
  // the older answer must not land after the newer one.
  const seq = useRef(0)

  const fetchEmails = useCallback(() => {
    setEmails(null)
    setFailed(false)
    const mine = ++seq.current
    budgetApi.listTenantEmails(row.id)
      .then((res) => { if (seq.current === mine) setEmails(res.emails.filter((e) => !e.primary)) })
      .catch(() => { if (seq.current === mine) setFailed(true) })
  }, [row.id])

  // Every fresh answer for this row (a search, a refresh when the page is shown
  // again) resets the panel, so it never lags the email line above it: open and
  // re-read when the search matched one of these addresses, closed otherwise.
  useEffect(() => {
    if (matched) { setOpen(true); fetchEmails() }
    else { ++seq.current; setOpen(false); setEmails(null); setFailed(false) }
  }, [row, matched, fetchEmails])

  const toggle = () => {
    if (open) { ++seq.current; setOpen(false); setEmails(null); setFailed(false) }
    else { setOpen(true); fetchEmails() }
  }

  return (
    <div className="px-3 pb-2 -mt-1 text-[12px]">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        // Every row has one of these: the row's email line says WHOSE.
        aria-describedby={`tenant-email-${row.id}`}
        className="text-blue-400 hover:underline inline-flex items-center gap-1 min-h-[28px] pr-2"
        data-testid="tenant-show-emails"
      >
        <span aria-hidden>{open ? '▾' : '▸'}</span>
        {open
          ? t('settings.platform.customers.hideEmails', { defaultValue: 'Hide other emails' })
          : row.otherEmailCount == null
            // An older response carries no count: say it without a number.
            ? t('settings.platform.customers.showEmailsNoCount', { defaultValue: 'Other emails' })
            : t('settings.platform.customers.showEmails', {
                defaultValue: 'Other emails ({{count}})',
                count: row.otherEmailCount,
              })}
      </button>
      {open && (
        <ul className="mt-1 ml-4 space-y-0.5" data-testid="tenant-all-emails">
          {failed && (
            <li className="text-red-400">{t('settings.platform.customers.emailsFailed', { defaultValue: 'Couldn’t load emails' })}</li>
          )}
          {!failed && emails === null && (
            <li className="themed-text-muted">{t('settings.platform.customers.emailsLoading', { defaultValue: 'Loading…' })}</li>
          )}
          {emails?.map((e) => {
            const isMatch = matched !== null && e.email.toLowerCase() === matched
            return (
              <li
                key={e.email}
                className={`break-all ${isMatch ? 'themed-text font-semibold' : 'themed-text-secondary'}`}
                data-matched={isMatch || undefined}
              >
                {e.email}
                {isMatch && (
                  <span className="sr-only"> ({t('settings.platform.customers.matchedEmail', { defaultValue: 'matches the search' })})</span>
                )}
                {e.verified === false && <UnverifiedChip />}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
